import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Monitor, Library, ChevronLeft, ChevronRight, FolderGit2, Bot } from 'lucide-react';
import SectionHeader from '@/components/shared/SectionHeader';
import { projects } from '@/data/portfolioConfig';

type Project = {
  id: string;
  title: string;
  description: { it: string; en: string };
  technologies: string[];
  category: string;
  highlights: { it: string[]; en: string[] };
};

type Filter = 'all' | 'ai' | 'desktop' | 'shared-library';

const categoryMeta: Record<string, {
  icon: React.ElementType;
  labelIt: string;
  labelEn: string;
  accent: string;
  bg: string;
  glow: string;
}> = {
  ai: {
    icon: Bot,
    labelIt: 'AI / GenAI',
    labelEn: 'AI / GenAI',
    accent: 'from-emerald-400 to-teal-500',
    bg: 'from-emerald-500/10 via-teal-500/5 to-transparent',
    glow: 'shadow-emerald-500/40',
  },
  desktop: {
    icon: Monitor,
    labelIt: 'Desktop App',
    labelEn: 'Desktop App',
    accent: 'from-cyan-400 to-primary',
    bg: 'from-cyan-500/10 via-primary/5 to-transparent',
    glow: 'shadow-primary/40',
  },
  'shared-library': {
    icon: Library,
    labelIt: 'Libreria C++',
    labelEn: 'C++ Library',
    accent: 'from-violet-400 to-purple-500',
    bg: 'from-violet-500/10 via-purple-500/5 to-transparent',
    glow: 'shadow-violet-500/40',
  },
};

const slideVariants = {
  enter: (dir: number) => ({
    x: dir > 0 ? '60%' : '-60%',
    opacity: 0,
    scale: 0.88,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: { type: 'spring' as const, stiffness: 260, damping: 28 },
  },
  exit: (dir: number) => ({
    x: dir > 0 ? '-60%' : '60%',
    opacity: 0,
    scale: 0.88,
    transition: { duration: 0.22, ease: 'easeIn' as const },
  }),
};

export default function Projects() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'it' | 'en';

  const allProjects = projects as Project[];
  const [filter, setFilter] = useState<Filter>('all');
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const dragStartX = useRef(0);

  const filtered = filter === 'all'
    ? allProjects
    : allProjects.filter(p => p.category === filter);

  const project = filtered[index] ?? filtered[0];
  const total = filtered.length;

  // Reset index when filter changes
  useEffect(() => { setIndex(0); }, [filter]);

  const go = useCallback((d: number) => {
    setDir(d);
    setIndex(prev => (prev + d + total) % total);
  }, [total]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  if (!project) return null;

  const meta = categoryMeta[project.category] ?? categoryMeta.desktop;
  const Icon = meta.icon;

  const aiCount = allProjects.filter(p => p.category === 'ai').length;
  const desktopCount = allProjects.filter(p => p.category === 'desktop').length;
  const libCount = allProjects.filter(p => p.category === 'shared-library').length;

  return (
    <section
      id="projects"
      className="section bg-surface dark:bg-dark-bg-subtle border-b-2 border-border-subtle dark:border-border-dark overflow-hidden"
    >
      <div className="max-w-[1200px] mx-auto px-4">

        <SectionHeader
          icon={FolderGit2}
          tag={t('projects.tag', 'Portfolio')}
          index={3}
          title={t('projects.title', 'Progetti')}
          subtitle={lang === 'it'
            ? 'Agenti AI in Python (RAG, MCP, multi-agente) e tool C++23/Qt6 per l\'ecosistema automotive: co-simulazione FMU/FMI, analisi segnali MDF/ASAM, orchestrazione SIL e toolchain AUTOSAR.'
            : 'Python AI agents (RAG, MCP, multi-agent) and C++23/Qt6 tools for the automotive ecosystem: FMU/FMI co-simulation, MDF/ASAM signal analysis, SIL orchestration, and AUTOSAR toolchains.'}
        />

        {/* Filter tabs */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {([
            { key: 'all', labelIt: `Tutti (${total === allProjects.length ? allProjects.length : allProjects.length})`, labelEn: `All (${allProjects.length})` },
            { key: 'ai', labelIt: `AI / GenAI (${aiCount})`, labelEn: `AI / GenAI (${aiCount})` },
            { key: 'desktop', labelIt: `Desktop (${desktopCount})`, labelEn: `Desktop (${desktopCount})` },
            { key: 'shared-library', labelIt: `Librerie (${libCount})`, labelEn: `Libraries (${libCount})` },
          ] as { key: Filter; labelIt: string; labelEn: string }[]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-5 py-2 rounded-full text-sm font-bold border-2 transition-all duration-200 ${
                filter === tab.key
                  ? 'bg-primary text-white dark:text-gray-900 border-primary shadow-lg shadow-primary/30'
                  : 'bg-transparent border-border dark:border-primary/20 text-text-muted dark:text-gray-400 hover:border-primary/50 hover:text-primary'
              }`}
            >
              {lang === 'it' ? tab.labelIt : tab.labelEn}
            </button>
          ))}
        </div>

        {/* Carousel */}
        <div className="relative">

          {/* Prev button */}
          <button
            onClick={() => go(-1)}
            aria-label="Previous project"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-4 z-20 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white dark:bg-dark-bg-elevated border-2 border-border dark:border-primary/20 shadow-xl hover:border-primary hover:shadow-primary/30 transition-all duration-200 group"
          >
            <ChevronLeft className="h-5 w-5 text-text-muted group-hover:text-primary transition-colors" />
          </button>

          {/* Next button */}
          <button
            onClick={() => go(1)}
            aria-label="Next project"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-4 z-20 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white dark:bg-dark-bg-elevated border-2 border-border dark:border-primary/20 shadow-xl hover:border-primary hover:shadow-primary/30 transition-all duration-200 group"
          >
            <ChevronRight className="h-5 w-5 text-text-muted group-hover:text-primary transition-colors" />
          </button>

          {/* Card stage */}
          <div
            className="overflow-hidden mx-8 sm:mx-12"
            onPointerDown={e => { dragStartX.current = e.clientX; }}
            onPointerUp={e => {
              const dx = e.clientX - dragStartX.current;
              if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
            }}
          >
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={`${filter}-${index}`}
                custom={dir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className={`relative bg-gradient-to-br ${meta.bg} border-2 border-border dark:border-primary/20 rounded-3xl overflow-hidden shadow-2xl ${meta.glow}`}
                style={{ minHeight: '420px' }}
              >
                {/* Ambient glow background */}
                <div className={`absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gradient-to-br ${meta.accent} opacity-10 dark:opacity-15 blur-3xl pointer-events-none`} />

                <div className="relative z-10 p-6 sm:p-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">

                  {/* Left column */}
                  <div className="flex flex-col">
                    {/* Category badge */}
                    <div className="flex items-center gap-3 mb-5">
                      <div className={`p-2.5 rounded-xl bg-gradient-to-br ${meta.accent} shadow-lg`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <span className={`text-xs font-black uppercase tracking-widest bg-gradient-to-r ${meta.accent} bg-clip-text text-transparent`}>
                        {lang === 'it' ? meta.labelIt : meta.labelEn}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl sm:text-3xl font-black text-text-primary dark:text-white mb-4 leading-tight">
                      {project.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm sm:text-base text-text-secondary dark:text-text-light leading-relaxed mb-6">
                      {project.description[lang]}
                    </p>

                    {/* Tech tags */}
                    <div className="flex flex-wrap gap-2 mt-auto">
                      {project.technologies.map(tech => (
                        <span
                          key={tech}
                          className="px-3 py-1 text-[11px] font-bold rounded-lg bg-white/80 dark:bg-dark-bg-elevated text-text-secondary dark:text-gray-300 border border-border dark:border-primary/20 tracking-wide"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Right column - highlights */}
                  <div className="flex flex-col justify-center">
                    <p className="text-[10px] font-black uppercase tracking-widest text-text-muted dark:text-gray-500 mb-4">
                      {lang === 'it' ? 'Caratteristiche principali' : 'Key features'}
                    </p>
                    <ul className="space-y-3">
                      {project.highlights[lang].map((h, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: 16 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.07, type: 'spring', stiffness: 200 }}
                          className="flex items-start gap-3"
                        >
                          <div className={`mt-1.5 w-1.5 h-1.5 rounded-full bg-gradient-to-br ${meta.accent} flex-shrink-0`} />
                          <span className="text-sm text-text-secondary dark:text-text-light leading-relaxed">
                            {h}
                          </span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Counter badge */}
                <div className="absolute bottom-4 right-5 text-xs font-bold text-text-muted dark:text-gray-600 tabular-nums">
                  {index + 1} / {total}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Dot strip */}
        <div className="flex justify-center gap-2 mt-7 flex-wrap px-4">
          {filtered.map((p, i) => (
            <button
              key={p.id}
              onClick={() => { setDir(i > index ? 1 : -1); setIndex(i); }}
              aria-label={p.title}
              title={p.title}
              className={`transition-all duration-300 rounded-full ${
                i === index
                  ? 'w-7 h-2.5 bg-primary'
                  : 'w-2.5 h-2.5 bg-border dark:bg-primary/20 hover:bg-primary/50'
              }`}
            />
          ))}
        </div>

        {/* Project title strip (desktop only) */}
        <div className="hidden sm:flex justify-center gap-1 mt-4 flex-wrap px-4">
          {filtered.map((p, i) => (
            <button
              key={p.id}
              onClick={() => { setDir(i > index ? 1 : -1); setIndex(i); }}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all duration-200 ${
                i === index
                  ? 'text-primary bg-primary/10'
                  : 'text-text-muted dark:text-gray-600 hover:text-primary hover:bg-primary/5'
              }`}
            >
              {p.title}
            </button>
          ))}
        </div>

        {/* Note */}
        <p className="text-center text-xs sm:text-sm text-text-muted dark:text-gray-500 mt-10 px-4">
          {lang === 'it'
            ? 'Progetti sviluppati per scopo personale e formativo - non disponibili open source.'
            : 'Projects developed for personal and educational purposes - not available open source.'}
        </p>
      </div>
    </section>
  );
}
